import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Activity, Clock, DollarSign } from "lucide-react";
import React, { useMemo } from "react";

interface JobOverview {
  totalCost?: number | string;
  efforts?: string;
}

interface ServiceOrderData {
  status?: string;
  soName?: string;
}

interface KPIMetric {
  title: string;
  count: string;
  trendSummary: string;
  icon: React.ReactNode;
  tooltip?: string;
}

// Configuration constants
const CONFIG = {
  defaults: {
    currency: "$",
    fallbackText: "N/A",
    fallbackCost: "0",
    status: "Completed",
  },
};

// Utility function
const formatValue = (
  value: number | string | undefined,
  formatter = "text",
  currency = CONFIG.defaults.currency
): string => {
  switch (formatter) {
    case "currency":
      return `${currency} ${value || CONFIG.defaults.fallbackCost}`;
    case "text":
      return String(value || CONFIG.defaults.fallbackText);
    default:
      return String(value || CONFIG.defaults.fallbackText);
  }
};

// Utility to get value from nested object path
const getValueFromPath = (obj: any, path: string): any => {
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

// KPI calculation logic
export const calculateHistoryKPIData = (
  jobOverview?: JobOverview,
  serviceOrderData?: ServiceOrderData,
  currency?: string
) => {
  const data = { serviceOrderData, jobOverview };

  return {
    totalCost: formatValue(
      getValueFromPath(data, "jobOverview.totalCost"),
      "currency",
      currency
    ),
    totalTime: formatValue(
      getValueFromPath(data, "jobOverview.efforts"),
      "text"
    ),
    status: formatValue(
      getValueFromPath(data, "serviceOrderData.status") ||
        CONFIG.defaults.status,
      "text"
    ),
  };
};

// KPI configuration
export const createHistoryKPIMetrics = (kpiData: {
  totalCost: string;
  totalTime: string;
  status: string;
}): KPIMetric[] => [
  {
    title: "Total Cost",
    count: kpiData.totalCost,
    trendSummary: "Actual cost for completed work",
    icon: <DollarSign className="h-8 w-8 text-green-500" />,
    tooltip: "Total cost incurred for the completed service order",
  },
  {
    title: "Total Time",
    count: kpiData.totalTime,
    trendSummary: "Time spent on work completion",
    icon: <Clock className="h-8 w-8 text-blue-500" />,
    tooltip: "Total time taken to complete all tasks",
  },
  {
    title: "Status",
    count: kpiData.status,
    trendSummary: "Service order status",
    icon: <Activity className="h-8 w-8 text-orange-500" />,
    tooltip: "Current status of the service order",
  },
];

interface HistoryServiceOrderKPIProps {
  jobOverview?: JobOverview;
  serviceOrderData?: ServiceOrderData;
  className?: string;
}

// Reusable KPI Dashboard Component
export const HistoryServiceOrderKPI: React.FC<HistoryServiceOrderKPIProps> = ({
  jobOverview,
  serviceOrderData,
  className = "",
}) => {
  const currency = useCurrency();
  const kpiData = useMemo(
    () => calculateHistoryKPIData(jobOverview, serviceOrderData, currency),
    [jobOverview, serviceOrderData]
  );

  const kpiMetrics = useMemo(() => createHistoryKPIMetrics(kpiData), [kpiData]);

  return (
    <div className={`grid gap-6 md:grid-cols-3 ${className}`}>
      {kpiMetrics.map((metric, index) => (
        <QuickStatsCard
          key={index}
          title={metric.title}
          count={metric.count}
          trendSummary={metric.trendSummary}
          icon={metric.icon}
          tooltip={metric.tooltip}
        />
      ))}
    </div>
  );
};

export default HistoryServiceOrderKPI;
